import { reactive, readonly } from 'vue'

// Tolerate a corrupted/legacy todo_user value (e.g. the literal string "undefined")
// instead of throwing at module load, which would white-screen the whole app —
// including the login page — and leave the user with no way back in.
function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('todo_user') || 'null')
  } catch {
    localStorage.removeItem('todo_user')
    return null
  }
}

const state = reactive({
  user:  readStoredUser(),
  token: localStorage.getItem('todo_token') || null,
})

export function useAuth() {
  function setSession(token, user) {
    state.token = token
    state.user  = user
    localStorage.setItem('todo_token', token)
    localStorage.setItem('todo_user', JSON.stringify(user))
  }

  function clearSession() {
    state.token = null
    state.user  = null
    localStorage.removeItem('todo_token')
    localStorage.removeItem('todo_user')
  }

  // Overwrite the cached user with an authoritative server record (from /auth/me).
  // The UI gates (isManager, isSuperadmin) read this object, so refreshing it on
  // boot reverts any client-side tampering of role/flags in localStorage. Note
  // this is defence-in-depth only — the API always re-derives role from the token.
  function setUser(user) {
    if (!user || typeof user !== 'object') return
    state.user = user
    localStorage.setItem('todo_user', JSON.stringify(user))
  }

  const isLoggedIn      = () => !!state.token
  const isManager       = () => state.user?.role === 'manager'
  const isSuperadmin    = () => state.user?.is_superadmin === true
  const isExternalClient = () => state.user?.user_type === 'external'
  const isInternal      = () => isLoggedIn() && state.user?.user_type !== 'external'
  const currentUser     = () => state.user

  return { state: readonly(state), setSession, setUser, clearSession, isLoggedIn, isManager, isSuperadmin, isExternalClient, isInternal, currentUser }
}
